import { eloSelectionOpacity } from "./C1_eloSelectionFocus";
import type { C1_Floor } from "../model/C1_Floor";
import type { C1_Project } from "../model/C1_Project";
import type { C1_Room } from "../model/C1_Room";
import { isSpecialExteriorRoomType, type WallSide } from "../types/C1_ModelTypes";
import { hasContinuousExteriorWalls } from "../types/C1_RoomTypes";
import { roomWallThickness } from "../model/C1_wallThickness";
import type { ResizePreview } from "../types/C1_AppTypes";
import c1_state from "../model/C1_State";
import { useModel } from "../model/C1_useModel";
import { lightenHexColor } from "../lib/C1_color";
import { C1_FURNISHING_WALL_LIGHTEN_FACTOR } from "../model/C1_ModelSettings";
import { usesFurnishingPlanPresentation } from "./C1_editModePresentation";
import { roomPreviewPoints } from "./C1_roomPreviewGeometry";

type WallLayerProps = {
  project: C1_Project;
  floor: C1_Floor;
  exterior: boolean;
  resizePreview?: ResizePreview | null;
  highlightedWallIds?: string[];
  structureOpacity?: number;
};

export function C1_WallLayer({ project, floor, exterior: layerExterior, resizePreview, highlightedWallIds = [], structureOpacity = 1 }: WallLayerProps) {
  useModel(c1_state);
  const view = c1_state.view;
  const PX = c1_state.svgPx;
  const roomElements = createWallLayerElements(project, floor, layerExterior, resizePreview, view, PX, highlightedWallIds, structureOpacity);

  return (
    <g
      className={`C1_wallLayer ${layerExterior ? "C1_exteriorWallLayer" : "C1_interiorWallLayer"}`}
      data-c1-svg-layer={layerExterior ? "exterior-walls" : "interior-walls"}
    >
      {roomElements}
    </g>
  );
}

type WallRect = {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
};

type WallSegment = ReturnType<C1_Room["wallSegments"]>[number];

type WallGeometry = {
  x: number;
  y: number;
  width: number;
  height: number;
};

/** Builds the complete wall layer without hiding traversal inside array callbacks. */
function createWallLayerElements(
  project: C1_Project,
  floor: C1_Floor,
  layerExterior: boolean,
  resizePreview: ResizePreview | null | undefined,
  view: WallRect,
  PX: number,
  highlightedWallIds: string[],
  structureOpacity: number,
): JSX.Element[] {
  if (!floor) return [];
  const elements: JSX.Element[] = [];

  for (const room of floor.rooms) {
    elements.push(
      ...createPolygonWallElements(room, project, layerExterior, resizePreview, view, PX, highlightedWallIds, structureOpacity),
    );
  }

  return elements;
}

/** Renders arbitrary ordered wall polygons directly from their true endpoints. */
function createPolygonWallElements(
  room: C1_Room,
  project: C1_Project,
  layerExterior: boolean,
  resizePreview: ResizePreview | null | undefined,
  view: WallRect,
  PX: number,
  highlightedWallIds: string[],
  structureOpacity: number,
): JSX.Element[] {
  const elements: JSX.Element[] = [];
  if (!room.walls) return elements;
  const previewPoints = roomPreviewPoints(room, resizePreview, c1_state.drag.value);
  const previewActive = resizePreview?.room === room && previewPoints.length >= 2;
  const wallCount = previewActive ? previewPoints.length - 1 : room.walls.size;
  for (let index = 0; index < wallCount; index += 1) {
    const wall = room.walls.at(Math.min(index, room.walls.size - 1));
    if (!wall) continue;
    const exterior = room.isExteriorSegment(wall);
    if (exterior !== layerExterior) continue;
    const directSelection = c1_state.selectedWall;
    const directlySelected = directSelection !== undefined && (wall === directSelection || wall.partnerWall === directSelection);
    const editingHiddenWall = !c1_state.floorImageRenderOptions.value && c1_state.editMode.value === "building";
    if (!wall.visible && !c1_state.floorImageRenderOptions.value?.roomPlanExport && !editingHiddenWall) continue;
    let points = previewActive
      ? { start: previewPoints[index], end: previewPoints[index + 1] }
      : room.wallPoints(wall);
    if (!points) continue;
    const drag = c1_state.drag.value;
    const previewsPartnerWall =
      drag?.type === "resize" &&
      resizePreview?.wallCoordinate !== undefined &&
      wall.partnerWall?.id === drag.wallId;
    if (previewsPartnerWall) {
      const coordinate = resizePreview.wallCoordinate!;
      points =
        drag.side === "left" || drag.side === "right"
          ? { start: { ...points.start, x: coordinate }, end: { ...points.end, x: coordinate } }
          : { start: { ...points.start, y: coordinate }, end: { ...points.end, y: coordinate } };
    }
    const specialRoomWithoutNormalNeighbor =
      isSpecialExteriorRoomType(room.type) && !room.wallInheritsExteriorFromNormalRoom(wall);
    const color = exterior ? project.settings.wallColorExterior : project.settings.wallColorInterior;
    const selectedWall = !c1_state.floorImageRenderOptions.value && directlySelected;
    const draggedWallId = c1_state.drag.value?.type === "resize" ? c1_state.drag.value.wallId : undefined;
    const hovered = !c1_state.floorImageRenderOptions.value && c1_state.editMode.value === "building" &&
      (highlightedWallIds.includes(wall.id) || (wall.partnerWall !== undefined && highlightedWallIds.includes(wall.partnerWall.id)));
    const highlighted = !c1_state.floorImageRenderOptions.value && (
      hovered ||
      draggedWallId === wall.id ||
      (draggedWallId !== undefined && draggedWallId === wall.partnerWall?.id));
    const interactionColor = selectedWall ? "#ffa500" : hovered ? "#fed7aa" : undefined;
    elements.push(
      <line
        key={`${wall.id}-${index}`}
        opacity={eloSelectionOpacity(room, c1_state) * (
          selectedWall || c1_state.selection === room ||
          (drag?.type === "room" && drag.roomId === room.id) ||
          (c1_state.selection !== undefined && c1_state.selection === wall.partnerWall?.parent?.c1) ||
          (draggedWallId !== undefined && (draggedWallId === wall.id || draggedWallId === wall.partnerWall?.id))
            ? 1 : structureOpacity
        )}
        className={highlighted ? "C1_wallHighlighted" : undefined}
        data-c1-room-id={room.id}
        data-c1-wall-id={wall.id}
        x1={(points.start.x - view.x1) * PX}
        y1={(points.start.y - view.y1) * PX}
        x2={(points.end.x - view.x1) * PX}
        y2={(points.end.y - view.y1) * PX}
        stroke={interactionColor ?? (specialRoomWithoutNormalNeighbor ? "#64748b" : wall.visible ? furnishingColor(color) : "#94a3b8")}
        strokeWidth={specialRoomWithoutNormalNeighbor ? 4 : wall.visible ? roomWallThickness(room, wall, project) * PX : 1}
        strokeDasharray={specialRoomWithoutNormalNeighbor ? "12 8" : wall.visible ? undefined : "5 4"}
        strokeLinecap="square"
      />,
    );
  }
  return elements;
}

/** Determines whether the wall segment at a position belongs to the exterior shell. */
function isExterior(room: C1_Room, side: WallSide, project: C1_Project, position?: number): boolean {
  const segments = room.wallSegments(side, project);
  if (segments.length === 0) return true;

  const testPosition = position ?? (segments[0].start + segments[segments.length - 1].end) / 2;
  for (let index = 0; index < segments.length; index += 1) {
    const segment = segments[index];
    const includesEnd = index === segments.length - 1;
    if (testPosition >= segment.start && (testPosition < segment.end || includesEnd)) return segment.exterior;
  }

  return true;
}

/** Collects IDs of rooms that overlap the hidden wall on its opposite side. */
function findPartnerRoomIds(
  room: C1_Room,
  side: WallSide,
  segment: WallSegment,
  roomRect: WallRect,
  floor: C1_Floor,
  project: C1_Project,
  tolerance: number,
): string[] {
  const partnerRoomIds: string[] = [];
  const vertical = side === "left" || side === "right";

  for (const candidate of floor.rooms) {
    if (candidate.id === room.id) continue;

    const candidateRect = candidate.rect();
    if (!roomsShareWallLine(side, roomRect, candidateRect, tolerance)) continue;

    const candidateStart = vertical ? candidateRect.y1 : candidateRect.x1;
    const candidateEnd = vertical ? candidateRect.y2 : candidateRect.x2;
    const overlapsSegment = candidateStart < segment.end - tolerance && candidateEnd > segment.start + tolerance;
    if (overlapsSegment) partnerRoomIds.push(candidate.id);
  }

  return partnerRoomIds;
}

/** Checks only the wall axis; overlap along the wall is checked separately. */
function roomsShareWallLine(side: WallSide, roomRect: WallRect, candidateRect: WallRect, tolerance: number): boolean {
  if (side === "left") return Math.abs(candidateRect.x2 - roomRect.x1) <= tolerance;
  if (side === "right") return Math.abs(candidateRect.x1 - roomRect.x2) <= tolerance;
  if (side === "top") return Math.abs(candidateRect.y2 - roomRect.y1) <= tolerance;
  return Math.abs(candidateRect.y1 - roomRect.y2) <= tolerance;
}

/** Creates all visible SVG elements for one side of one room. */
function wallSvg(
  room: C1_Room,
  side: WallSide,
  project: C1_Project,
  floor: C1_Floor,
  layerExterior: boolean,
  roomRect: WallRect,
  rect: WallRect,
  view: WallRect,
  PX: number,
): Array<JSX.Element> {
  const elements: JSX.Element[] = [];

  for (const segment of room.wallSegments(side, project)) {
    const hidden = room.isWallHidden(side, project, segment.start, segment.end);
    if (!shouldRenderSegment(segment, hidden, layerExterior)) continue;

    const geometry = calculateWallGeometry(room, side, segment, project, roomRect, rect, view, PX);
    if (!geometry) continue;

    elements.push(createWallElement(room, side, segment, geometry, hidden, project, floor, roomRect));
  }

  return elements;
}

// Retained temporarily for the PDF/export geometry helpers that will be migrated separately.
void wallSvg;

/** Applies export visibility and exterior-layer rules to one segment. */
function shouldRenderSegment(segment: WallSegment, hidden: boolean, layerExterior: boolean): boolean {
  const roomPlanExport = Boolean(c1_state.floorImageRenderOptions.value?.roomPlanExport);
  if (segment.inheritedFromNormalRoom && !roomPlanExport) return false;
  if (hidden && !roomPlanExport) return false;
  return segment.exterior === layerExterior;
}

/** Converts a model wall segment into its final SVG rectangle. */
function calculateWallGeometry(
  room: C1_Room,
  side: WallSide,
  segment: WallSegment,
  project: C1_Project,
  roomRect: WallRect,
  previewRect: WallRect,
  view: WallRect,
  PX: number,
): WallGeometry | null {
  const vertical = side === "left" || side === "right";
  const modelStart = vertical ? roomRect.y1 : roomRect.x1;
  const modelEnd = vertical ? roomRect.y2 : roomRect.x2;
  const previewStart = vertical ? previewRect.y1 : previewRect.x1;
  const previewEnd = vertical ? previewRect.y2 : previewRect.x2;
  const segmentStart = segment.start === modelStart ? previewStart : segment.start;
  const segmentEnd = segment.end === modelEnd ? previewEnd : segment.end;
  if (segmentEnd <= segmentStart) return null;

  const thickness = wallThickness(segment.exterior, project, PX);
  const topThickness = wallThickness(isExterior(room, "top", project, roomRect.x1), project, PX);
  const bottomThickness = wallThickness(isExterior(room, "bottom", project, roomRect.x1), project, PX);
  const leftThickness = wallThickness(isExterior(room, "left", project, roomRect.y1), project, PX);
  const rightThickness = wallThickness(isExterior(room, "right", project, roomRect.y1), project, PX);

  if (side === "left" || side === "right") {
    const topExtension = segment.start === roomRect.y1 ? topThickness / 2 : segment.exterior ? thickness / 2 : 0;
    const bottomExtension = segment.end === roomRect.y2 ? bottomThickness / 2 : segment.exterior ? thickness / 2 : 0;
    const roomX = side === "left" ? previewRect.x1 : previewRect.x2;
    return {
      x: (roomX - view.x1) * PX - thickness / 2,
      y: (segmentStart - view.y1) * PX - topExtension,
      width: thickness,
      height: (segmentEnd - segmentStart) * PX + topExtension + bottomExtension,
    };
  }

  const leftExtension = segment.start === roomRect.x1 ? leftThickness / 2 : segment.exterior ? thickness / 2 : 0;
  const rightExtension = segment.end === roomRect.x2 ? rightThickness / 2 : segment.exterior ? thickness / 2 : 0;
  const roomY = side === "top" ? previewRect.y1 : previewRect.y2;
  return {
    x: (segmentStart - view.x1) * PX - leftExtension,
    y: (roomY - view.y1) * PX - thickness / 2,
    width: (segmentEnd - segmentStart) * PX + leftExtension + rightExtension,
    height: thickness,
  };
}

/** Returns the configured wall thickness converted to SVG pixels. */
function wallThickness(exterior: boolean, project: C1_Project, PX: number): number {
  const modelThickness = exterior ? project.settings.wallThicknessExterior : project.settings.wallThicknessInterior;
  return modelThickness * PX;
}

/** Selects the correct SVG representation for one already validated segment. */
function createWallElement(
  room: C1_Room,
  side: WallSide,
  segment: WallSegment,
  geometry: WallGeometry,
  hidden: boolean,
  project: C1_Project,
  floor: C1_Floor,
  roomRect: WallRect,
): JSX.Element {
  if (hidden) return hiddenWallSvg(room, side, segment, geometry, project, floor, roomRect);
  if (hasContinuousExteriorWalls(room.type)) return standardWallSvg(room, side, segment, geometry, project);
  if (isSpecialExteriorRoomType(room.type) && !segment.inheritedFromNormalRoom) {
    return specialExteriorWallSvg(room, side, segment, geometry);
  }
  return standardWallSvg(room, side, segment, geometry, project);
}

/** Renders a hidden wall as a dashed center line for room-plan exports. */
function hiddenWallSvg(
  room: C1_Room,
  side: WallSide,
  segment: WallSegment,
  geometry: WallGeometry,
  project: C1_Project,
  floor: C1_Floor,
  roomRect: WallRect,
): JSX.Element {
  const vertical = side === "left" || side === "right";
  const partnerRoomIds = findPartnerRoomIds(room, side, segment, roomRect, floor, project, 0.001);
  return (
    <line
      key={`${room.id}_${side}_${segment.start}_${segment.end}_hidden`}
      data-c1-room-id={room.id}
      data-c1-hidden-wall="true"
      data-c1-partner-room-ids={partnerRoomIds.join(",")}
      x1={vertical ? geometry.x + geometry.width / 2 : geometry.x}
      y1={vertical ? geometry.y : geometry.y + geometry.height / 2}
      x2={vertical ? geometry.x + geometry.width / 2 : geometry.x + geometry.width}
      y2={vertical ? geometry.y + geometry.height : geometry.y + geometry.height / 2}
      fill="none"
      stroke="#94a3b8"
      strokeWidth={1}
      strokeDasharray="5 4"
    />
  );
}

/** Renders boundaries of special exterior room types as dashed center lines. */
function specialExteriorWallSvg(
  room: C1_Room,
  side: WallSide,
  segment: WallSegment,
  geometry: WallGeometry,
): JSX.Element {
  const vertical = side === "left" || side === "right";
  return (
    <line
      key={`${room.id}_${side}_${segment.start}_${segment.end}_special`}
      data-c1-room-id={room.id}
      data-c1-special-room-wall="true"
      x1={vertical ? geometry.x + geometry.width / 2 : geometry.x}
      y1={vertical ? geometry.y : geometry.y + geometry.height / 2}
      x2={vertical ? geometry.x + geometry.width / 2 : geometry.x + geometry.width}
      y2={vertical ? geometry.y + geometry.height : geometry.y + geometry.height / 2}
      fill="none"
      stroke="#64748b"
      strokeWidth={4}
      strokeDasharray="12 8"
      strokeLinecap="butt"
    />
  );
}

/** Renders an ordinary interior or exterior wall rectangle. */
function standardWallSvg(
  room: C1_Room,
  side: WallSide,
  segment: WallSegment,
  geometry: WallGeometry,
  project: C1_Project,
): JSX.Element {
  const baseFill = segment.exterior ? project.settings.wallColorExterior : project.settings.wallColorInterior;
  return (
    <rect
      key={`${room.id}_${side}_${segment.start}_${segment.end}`}
      data-c1-room-id={room.id}
      {...geometry}
      fill={furnishingColor(baseFill)}
      stroke="#333"
      strokeWidth="0"
    />
  );
}

/** Lightens wall colors consistently in all equipment-focused modes. */
function furnishingColor(baseColor: string): string {
  if (!usesFurnishingPlanPresentation(c1_state.editMode.value)) return baseColor;
  return lightenHexColor(baseColor, C1_FURNISHING_WALL_LIGHTEN_FACTOR);
}
